import React from "react";
import {
  IconBed,
  IconBath,
  IconTag,
  IconFlame,
  IconSparkles,
  IconRuler2,
  IconBuildingStore,
  IconMapPin,
  IconRuler,
} from "@tabler/icons-react";
import {
  PropertyTypeLabels,
  PropertyCurrencies,
} from "@/types/property-config";
import { Property } from "@/types/property";
import { categorizeProperty } from "@/utils/helpers/categorize-property";
import { Pricetag, VerifiedBadge } from "@/components/bnp-icons";
import { useCurrency } from "@/hooks/useCurrency";
import { PropertyService } from "@/services";

// utils/toTitleCase.ts
export const toTitleCase = (str: string = "") => {
  return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
};

interface PropertyListingCardProps {
  property: Property;
  onCardClick?: (property: Property) => void;
}

// Currency Symbol Helper
const getCurrencySymbol = (currency?: PropertyCurrencies): string => {
  switch (currency) {
    case PropertyCurrencies.USD:
      return "$";
    case PropertyCurrencies.GBP:
      return "£";
    case PropertyCurrencies.NGN:
    default:
      return "₦";
  }
};

const getListingTypeActionLabel = (listingType?: string): string => {
  switch (listingType?.toLowerCase()) {
    case "sale":
      return "Buy";
    case "rent":
      return "Rent";
    case "shortlet":
      return "Shortlet";
    case "lease":
      return "Lease";
    default:
      return "Price";
  }
};

// Relative Time Helper ("an hour ago", "2 hours ago", etc.)
const getRelativeTime = (dateString: string): string => {
  if (!dateString) return "";
  const now = new Date();
  const created = new Date(dateString);
  const diffInMinutes = Math.floor(
    (now.getTime() - created.getTime()) / (1000 * 60),
  );

  if (diffInMinutes < 1) return "Just now";
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours === 1) return "an hour ago";
  if (diffInHours < 24) return `${diffInHours} hours ago`;

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays === 1) return "Yesterday";
  if (diffInDays < 30) return `${diffInDays} days ago`;

  const diffInMonths = Math.floor(diffInDays / 30);
  return `${diffInMonths} month${diffInMonths > 1 ? "s" : ""} ago`;
};

// Helper to check if property was listed within the last 48 hours
const isNewListing = (dateString: string): boolean => {
  if (!dateString) return false;
  const created = new Date(dateString);
  const now = new Date();
  const fortyEightHoursInMs = 48 * 60 * 60 * 1000;
  return now.getTime() - created.getTime() < fortyEightHoursInMs;
};

export const PropertyListingCard: React.FC<PropertyListingCardProps> = ({
  property,
  onCardClick,
}) => {
  const categorized = categorizeProperty(property);
  const { general, pricingDetails, stats, categoryBasedFields } = categorized;

  // Image Fallback or primary image
  const displayImage = general.image_url?.[0] || "/placeholder-property.jpg";

  // Capitalize Listing Type for display (e.g. "Sale", "Rent", "Shortlet")
  const listingTypeLabel = general.listing_type
    ? general.listing_type.charAt(0).toUpperCase() +
      general.listing_type.slice(1).toLowerCase()
    : "Sale";

  // Format Realtor Name
  const realtorName = general.realtor
    ? general.realtor?.business_name ||
      `${general.realtor?.first_name} ${general.realtor?.last_name}`
    : general.createdBy?.full_name || "N/A";

  // Location string
  const locationText =
    [general.address, general.city, general.state].filter(Boolean).join(", ") ||
    "N/A";

  // Property Type Label lookup
  const propertyTypeLabel = general.property_type
    ? PropertyTypeLabels[
        general.property_type as keyof typeof PropertyTypeLabels
      ] || general.property_type
    : "N/A";

  const { formatPrice } = useCurrency();

  const formattedPrice = formatPrice(
    pricingDetails.price,
    pricingDetails.currency,
  );
  const formattedTotalPackage = pricingDetails.total_package
    ? formatPrice(pricingDetails.total_package, pricingDetails.currency)
    : "N/A";

  const mediaUrl = displayImage;
  const isVideo = mediaUrl && /\.(mp4|webm|ogg|mov|m4v)$/i.test(mediaUrl);

  const propertyId = property?.id;

  const trackRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      if (!node || !propertyId) return undefined;

      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];

          if (entry.isIntersecting) {
            // Fire your event tracker
            PropertyService.TrackPropertyInsight(propertyId, "impression");
            observer.disconnect(); // Clean up so it only fires once
          }
        },
        {
          threshold: 0.5, // 50% visible before firing
        },
      );

      observer.observe(node);
      return () => observer.disconnect();
    },
    [propertyId],
  );

  return (
    <div
      ref={trackRef}
      onClick={() => onCardClick?.(property)}
      className="relative font-sans text-base w-full sm:w-[350px] min-w-[300px] max-w-[350px] bg-white rounded-lg shadow-lg overflow-hidden cursor-pointer flex flex-col"
    >
      {/* 1. TOP IMAGE & BADGES CONTAINER */}
      <div className="relative h-[200px] sm:h-[220px] w-full bg-gray-100">
        {isVideo ? (
          <video
            src={mediaUrl}
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
          />
        ) : (
          <img
            src={displayImage}
            alt={general.property_type || "Property Listing"}
            className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
          />
        )}

        {/* Top Left Tag: Listing Type (Sale / Rent / Shortlet / Lease) */}
        <div className="absolute z-10 top-3 left-3 flex justify-center font-bold items-center gap-2 bg-base-white text-bnp-dark p-2 sm:p-2 lg:p-0 text-sm min-w-[100px] min-h-[44px] sm:min-h-[28px] lg:w-[100px] lg:h-[28px] rounded-lg touch-manipulation">
          <Pricetag />
          <span className="font-bold">{listingTypeLabel}</span>
        </div>

        {/* Top Right Badges: "Deals" or "New Listing" */}
        <div className="absolute z-10 top-3 right-3 flex flex-col gap-2">
          {stats.promote_to_weekly_deals ? (
            <div className="flex justify-center items-center gap-2 bg-red-base p-2 lg:p-0 text-sm text-white rounded-lg min-w-[123px] min-h-[44px] sm:min-h-[28px] lg:w-[123px] lg:h-[28px] touch-manipulation">
              <IconFlame className="w-4 h-4 fill-white" />
              <span className="font-bold">Deals</span>
            </div>
          ) : isNewListing(general.created_at) ? (
            <div className="flex justify-center items-center gap-2 bg-bnp-dark p-2 lg:p-0 text-sm text-white rounded-lg min-w-[123px] min-h-[44px] sm:min-h-[28px] lg:w-[123px] lg:h-[28px] touch-manipulation">
              <IconSparkles className="w-4 h-4 text-white" />
              <span className="font-bold">New Listing</span>
            </div>
          ) : null}
        </div>

        {/* OVERLAPPING SPECIFICATIONS BAR (Dynamic per category) */}
        <div className="absolute -bottom-5 left-[2.5%] right-[2.5%] bg-white rounded-md shadow-sm border border-gray-100 px-4 py-4 flex items-center gap-2 sm:gap-4 text-sm font-bold text-soft-400 z-10">
          {/* CATEGORY BASED FIELD RENDERER */}
          {categoryBasedFields.category === "RESIDENTIAL" && (
            <div className="flex items-center gap-3 w-full overflow-x-auto no-scrollbar">
              {/* Bedroom Count */}
              <div className="flex items-center gap-1.5 shrink-0">
                <IconBed className="w-5 h-5 text-soft-400" />
                <span className="text-sm font-semibold text-gray-700">
                  {categoryBasedFields.data.bedroom_count ?? 0}
                </span>
              </div>

              <div className="border-r border-grey-5 h-4 shrink-0" />

              {/* Bathroom Count */}
              <div className="flex items-center gap-1.5 shrink-0">
                <IconBath className="w-5 h-5 text-soft-400" />
                <span className="text-sm font-semibold text-gray-700">
                  {categoryBasedFields.data.bathroom_count ?? 0}
                </span>
              </div>

              {/* Serviced Tag */}
              {categoryBasedFields.data.serviced && (
                <>
                  <div className="border-r border-grey-5 h-4 shrink-0" />
                  <span className="text-[11px] font-semibold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-md border border-teal-200/60 shrink-0">
                    Serviced
                  </span>
                </>
              )}

              {/* Shared Tag */}
              {categoryBasedFields.data.shared && (
                <>
                  <div className="border-r border-grey-5 h-4 shrink-0" />
                  <span className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-md border border-blue-200/60 shrink-0">
                    Shared
                  </span>
                </>
              )}
            </div>
          )}

          {categoryBasedFields.category === "COMMERCIAL" && (
            <>
              <div className="flex items-center gap-2">
                <IconBuildingStore className="w-5 h-5 text-soft-400" />
                <span>
                  {categoryBasedFields.data.total_floor_area_sqm
                    ? `${categoryBasedFields.data.total_floor_area_sqm} sqm`
                    : "N/A"}
                </span>
              </div>
              {categoryBasedFields.data.parking_capacity_cars !== undefined && (
                <>
                  <div className="border-r border-grey-5 h-4" />
                  <span>
                    {categoryBasedFields.data.parking_capacity_cars} Car Park
                    Capacity
                  </span>
                </>
              )}
            </>
          )}

          {categoryBasedFields.category === "LAND" && (
            <>
              <div className="flex items-center gap-2">
                <IconRuler2 className="w-5 h-5 text-soft-400" />
                <span>
                  {categoryBasedFields.data.number_of_plots
                    ? `${categoryBasedFields.data.number_of_plots} SQMs`
                    : "Land"}
                </span>
              </div>
            </>
          )}

          {categoryBasedFields.category === "INDUSTRIAL" && (
            <>
              <div className="flex items-center gap-2">
                <IconRuler className="w-5 h-5 text-soft-400" />
                <span>
                  {categoryBasedFields.data.total_land_area_sqm
                    ? `${categoryBasedFields.data.total_land_area_sqm} sqm`
                    : "Industrial"}
                </span>
              </div>
            </>
          )}

          {categoryBasedFields.category === "SPECIAL_PURPOSE" && (
            <>
              <div className="flex items-center gap-2">
                <IconMapPin className="w-5 h-5 text-soft-400" />
                <span>Special Purpose</span>
              </div>
            </>
          )}

          {categoryBasedFields.category === "OTHER" && (
            <div className="flex items-center gap-2">
              <span>Property Details</span>
            </div>
          )}
        </div>
      </div>

      {/* 2. CARD CONTENT BODY */}
      <div className="px-4 pt-6 pb-4 flex-1 flex flex-col justify-between">
        <div>
          {/* TITLE WITH CATEGORY BADGE */}
          {/* TOP METADATA BAR (Place directly above the title) */}
          <div className="flex items-center justify-between gap-2 mb-1.5 mt-2">
            {/* Category Label */}
            {general.property_category ? (
              <span className="text-[11px] font-semibold tracking-wide uppercase text-gray-500 bg-gray-100 px-2 py-0.5 rounded-md">
                {general.property_category?.replace(/_/g, " ")}
              </span>
            ) : (
              <span className="text-[11px] font-semibold tracking-wide uppercase text-gray-400 bg-gray-50 px-2 py-0.5 rounded-md">
                Property
              </span>
            )}

            {/* Time Ago Display */}
            <div className="flex items-center gap-1.5 text-xs text-gray-400 font-medium">
              <span className="px-4 py-1 mt-1 bg-bnp-cyan/15 text-bnp-cyan text-xs rounded-lg font-bold flex items-center whitespace-nowrap">
                Listed {getRelativeTime(general.created_at)}
              </span>
            </div>
          </div>

          <h3 className="text-base sm:text-lg font-bold text-dark-blue mb-1 line-clamp-1 flex-1">
            {toTitleCase(
              general.description ||
                `${propertyTypeLabel} for ${listingTypeLabel}`,
            )}
          </h3>

          {/* PRICE AND NEGOTIABLE BADGE */}
          <div className="flex items-center justify-between gap-2 mb-2">
            {/* MAIN PRICE DISPLAY (LHS) */}
            <div className="text-lg sm:text-xl font-bold text-bnp-cyan leading-snug">
              {formattedPrice}
            </div>

            {/* NEGOTIABLE BADGE (RHS) */}
            {pricingDetails.is_negotiable && (
              <span className="text-[10px] text-green-primary font-medium bg-emerald-50 border border-emerald-200/60 px-1.5 py-0.5 rounded">
                Negotiable
              </span>
            )}
          </div>
        </div>

        {/* 3. PROPERTY DETAILS TABLE / KEY-VALUE LIST */}
        <div className="space-y-1 sm:space-y-4 bg-gray-50 p-3 sm:p-4 rounded-lg text-sm min-h-[168px]">
          <div className="flex justify-between py-1 sm:py-0 items-center">
            <span className="text-grey-4 font-medium">Total Package</span>
            <span className="font-bold text-dark-blue">
              {formattedTotalPackage}
            </span>
          </div>

          <div className="flex justify-between py-1 sm:py-0 items-center">
            <span className="text-grey-4 font-medium">Listed By</span>
            <div className="flex items-center gap-1">
              {general.realtor?.is_partner === true && <VerifiedBadge />}
              <span className="font-bold text-dark-blue max-w-[150px] truncate">
                {realtorName}
              </span>
            </div>
          </div>

          <div className="flex justify-between py-1 sm:py-0 items-center">
            <span className="text-grey-4 font-medium">Property Type</span>
            <span className="font-bold text-dark-blue max-w-[150px] truncate">
              {propertyTypeLabel}
            </span>
          </div>

          <div className="flex justify-between py-1 sm:py-0 items-center">
            <span className="text-grey-4 font-medium">Location</span>
            <span className="font-bold text-dark-blue max-w-[150px] truncate">
              {locationText}
            </span>
          </div>
          {categoryBasedFields.category === "LAND" ? (
            /* --- LAND CATEGORY FIELDS --- */
            <>
              <div className="flex justify-between py-1 sm:py-0 items-center">
                <span className="text-grey-4 font-medium">Topography</span>
                <span className="font-bold text-dark-blue capitalize">
                  {categoryBasedFields.data.topography
                    ? categoryBasedFields.data.topography?.replace(/_/g, " ")
                    : "N/A"}
                </span>
              </div>
            </>
          ) : (
            /* --- OTHER CATEGORIES (Furnished Status) --- */
            <div className="flex justify-between py-1 sm:py-0 items-center">
              <span className="text-grey-4 font-medium">Furnished</span>
              <span className="font-bold text-dark-blue">
                {"furnished" in categoryBasedFields.data &&
                categoryBasedFields.data.furnished !== undefined
                  ? categoryBasedFields.data.furnished
                    ? "Yes"
                    : "No"
                  : property?.furnished !== undefined
                    ? property.furnished
                      ? "Yes"
                      : "No"
                    : "N/A"}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PropertyListingCard;
